import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MediaChapter {
  id: string;
  content_id: string;
  start_seconds: number;
  end_seconds: number;
  title: string;
  summary: string | null;
  created_at: string;
}

export function useMediaChapters(contentId: string | undefined) {
  return useQuery({
    queryKey: ['media-chapters', contentId],
    queryFn: async () => {
      if (!contentId) return [];
      
      const { data, error } = await supabase
        .from('media_chapters')
        .select('*')
        .eq('content_id', contentId)
        .order('start_seconds', { ascending: true });
      
      if (error) throw error;
      return data as MediaChapter[];
    },
    enabled: !!contentId,
  });
}

export function useChaptersStatus(contentId: string | undefined) {
  const queryClient = useQueryClient();
  const [realtimeStatus, setRealtimeStatus] = useState<string | null>(null);

  // Initial fetch
  const query = useQuery({
    queryKey: ['chapters-status', contentId],
    queryFn: async () => {
      if (!contentId) return null;
      
      const { data, error } = await supabase
        .from('content')
        .select('chapters_status')
        .eq('id', contentId)
        .single();
      
      if (error) throw error;
      return data?.chapters_status as string | null;
    },
    enabled: !!contentId,
  });

  // Realtime subscription for status updates
  useEffect(() => {
    if (!contentId) return;

    const channel = supabase
      .channel(`chapters-status-${contentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'content',
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const newStatus = (payload.new as any).chapters_status;
          console.log('[useChaptersStatus] Realtime update:', newStatus);
          setRealtimeStatus(newStatus);
          
          // If status changed to 'ready', refetch chapters data
          if (newStatus === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['media-chapters', contentId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contentId, queryClient]);

  // Return realtime status if available, otherwise query data
  return {
    ...query,
    data: realtimeStatus ?? query.data,
  };
}

export function useGenerateChapters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contentId: string) => {
      const { data, error } = await supabase.functions.invoke('generate-media-chapters', {
        body: { contentId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, contentId) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['media-chapters', contentId] });
      queryClient.invalidateQueries({ queryKey: ['chapters-status', contentId] });
    },
  });
}
